/**
 * The logo of a business (docs/08-entscheidungen.md, S13): one picture for every output, the printed
 * page, the PDF, the Word file and the menu on the net. The app turns what is uploaded (PNG, JPG,
 * SVG) into one PNG, at most LOGO_SIDE pixels on its longer side; the server checks it is one. On the
 * net it is frozen with the menu, like the texts: a new logo shows there after „Neu veröffentlichen“.
 */

export interface Logo {
  /** The PNG, base64. */
  data: string;
  width: number;
  height: number;
  /** When it was uploaded; marks it for the devices and for the digest of the menu on the net. */
  at: number;
}

/** The longer side of the PNG the app makes: sharp on paper at about 7 cm. */
export const LOGO_SIDE = 900;
/** The largest PNG the server keeps. */
export const MAX_LOGO_BYTES = 400_000;

const BASE64 = /^[A-Za-z0-9+/]+={0,2}$/;
const SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

/** The bytes a base64 text stands for, without decoding it. */
export const base64Bytes = (data: string) => Math.floor((data.length * 3) / 4) - (data.endsWith("==") ? 2 : data.endsWith("=") ? 1 : 0);

/** Width and height of a PNG given as base64, from its header; null: not a PNG. */
export function pngSize(data: string): { width: number; height: number } | null {
  if (data.length < 44 || !BASE64.test(data)) return null;
  const head = atob(data.slice(0, 44));
  const byte = (i: number) => head.charCodeAt(i);
  if (SIGNATURE.some((b, i) => byte(i) !== b) || head.slice(12, 16) !== "IHDR") return null;
  const u32 = (i: number) => ((byte(i) << 24) | (byte(i + 1) << 16) | (byte(i + 2) << 8) | byte(i + 3)) >>> 0;
  const width = u32(16);
  const height = u32(20);
  return width > 0 && height > 0 ? { width, height } : null;
}

/** A logo from what is stored or sent, or null: none, or not a PNG of a size the app makes. */
export function logoOf(v: unknown): Logo | null {
  const o = v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  if (typeof o.data !== "string" || base64Bytes(o.data) > MAX_LOGO_BYTES) return null;
  const size = pngSize(o.data);
  if (!size || size.width > 2 * LOGO_SIDE || size.height > 2 * LOGO_SIDE) return null;
  const at = typeof o.at === "number" && Number.isFinite(o.at) ? Math.round(o.at) : 0;
  return { data: o.data, ...size, at };
}

/** The size of a logo fitted into a box, keeping its shape; never larger than the box. */
export function fitLogo(logo: { width: number; height: number }, maxWidth: number, maxHeight: number): { width: number; height: number } {
  const scale = Math.min(maxWidth / logo.width, maxHeight / logo.height);
  return { width: logo.width * scale, height: logo.height * scale };
}

/** Where the logo stands: above the head, or at its left or right with the head beside it, left-aligned. */
export type LogoPlace = "center" | "left" | "right";
export const LOGO_PLACES: readonly LogoPlace[] = ["center", "left", "right"];
export const logoPlaceOf = (v: unknown): LogoPlace => (v === "left" || v === "right" ? v : "center");

/** Whether the name of the house stands under the logo: with „Logo statt Name“ only when there is no logo. */
export const showsHouse = (logo: unknown, logoOnly: boolean) => !(logo && logoOnly);
